import React, { useMemo } from 'react'

import {
  Button,
  HeadContent,
  Subsection,
  Table,
  Tag,
  useModalContext,
} from '@fourthwall/components'
import { PlusIcon } from '@fourthwall/icons'

import * as S from './PlatformApps.styled'
import { useCreatePlatformAppClient } from './hooks/useCreatePlatformAppClient'
import { useDeletePlatformAppClient } from './hooks/useDeletePlatformAppClient'
import { usePlatformAppClients } from './hooks/usePlatformAppClients'
import { AppDropdown } from './modules/AppDropdown/AppDropdown'
import { CreateAppModal } from './modules/CreateAppModal/CreateAppModal'
import type { App, AppScopeType } from './types'

export const PlatformApps = () => {
  const { open } = useModalContext()
  const { data: platformAppClients, isLoading: isLoadingPlatformAppClients } =
    usePlatformAppClients()
  const { deletePlatformAppClient, isLoading: isLoadingDeletePlatformAppClient } =
    useDeletePlatformAppClient()
  const { createPlatformAppClient, isLoading: isLoadingCreatePlatformAppClient } =
    useCreatePlatformAppClient()

  const isLoading =
    isLoadingPlatformAppClients ||
    isLoadingDeletePlatformAppClient ||
    isLoadingCreatePlatformAppClient

  const apps = useMemo(() => {
    return (
      platformAppClients
        ?.toSorted((a, b) => a.displayName.localeCompare(b.displayName))
        .map((client) => ({
          id: client.id,
          name: client.displayName,
          status: client.status,
          description: client.description,
          iconUrl: client.iconUrl,
          scopes: client.scopes.map((scope) => scope.id as AppScopeType),
          redirectUris: client.redirectUris,
          hmacKey: '',
          sections: [],
          createdAt: new Date(),
          updatedAt: new Date(),
        })) || []
    )
  }, [platformAppClients])

  const handleAddApp = () => {
    open(CreateAppModal, {
      onSave: (name: string, description: string, redirectUris: string[]) => {
        createPlatformAppClient([
          { displayName: name, description, redirectUris, scopes: ['shop_read'] },
        ])
      },
    })
  }

  const handleDelete = (app: App) => {
    deletePlatformAppClient([{ clientId: app.id }])
  }

  const headers = [
    {
      label: ' ',
      id: 'icon',
      width: 40,
      render: (row: App) =>
        row.iconUrl ? (
          <S.AppNameCell>
            <S.AppIcon src={row.iconUrl} alt={row.name} />
          </S.AppNameCell>
        ) : (
          <div />
        ),
    },
    {
      label: 'App Name',
      id: 'name',
      minWidth: 350,
      render: (row: App) => (
        <S.AppInfo>
          <S.AppName>{row.name}</S.AppName>
          {row.description && <S.AppDescription>{row.description}</S.AppDescription>}
        </S.AppInfo>
      ),
    },
    {
      label: 'Status',
      id: 'status',
      width: 40,
      render: (row: App) => {
        if (row.status === 'PUBLISHED') {
          return <Tag appearance="success" label="Published" />
        }
        if (row.status === 'SUBMITTED') {
          return <Tag appearance="alert" label="Submitted" />
        }

        return <Tag appearance="default" label="Draft" />
      },
    },
    {
      label: ' ',
      id: 'actions',
      width: 20,
      render: (row: App) => <AppDropdown app={row} onDelete={handleDelete} />,
    },
  ]

  return (
    <>
      <HeadContent title="Managed Apps &ndash; Settings" />

      <Subsection title="Your Apps">
        <S.AppsContainer>
          <S.HeaderContainer>
            <div>
              <S.DescriptionText>Manage your apps and their configurations.</S.DescriptionText>
            </div>
            <Button
              label="Create App"
              icon={<PlusIcon />}
              appearance="primary"
              onClick={handleAddApp}
            />
          </S.HeaderContainer>
          <S.TableContainer>
            <Table
              headers={headers}
              data={apps}
              isLoading={isLoading}
              emptyMessage="You don't have any apps yet."
              minWidth={500}
            />
          </S.TableContainer>
        </S.AppsContainer>
      </Subsection>
    </>
  )
}
